import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UPDATE_DEFAULTS_NOT_SAVED_ERROR } from 'constants/messageConstants';
import {
  clearMOrgAccessCache,
  isForbidden,
  useResolveMOrgId,
} from 'hooks/useResolveMOrgId';
import { MasterDbService } from 'services/MasterDbServices';
import {
  buildMBase,
  buildMWriteBody,
  readDeviceId,
} from 'utils/mDocumentFactory';

import {
  ADD_PRODUCT_CONFIG,
  AddProductKind,
  AddRecordValues,
  buildAddRecordFields,
  buildAddRecordOption,
} from './addRecord';
import type { StepSelectorOption } from './stepConfig';

interface CreateMRecordArgs {
  /**
   * Generated once when the form opens and reused on retry: the server rejects
   * a duplicate id, so a resend can't create two records.
   */
  id: string;
  values: AddRecordValues;
}

/**
 * Creates a record in the user's own M list from a picker's Add form
 * (OP-2498) and returns the option to select straight away. Failures are
 * reported inline by the form (the request is silent), and the form keeps its
 * values.
 */
export const useCreateMRecord = (kind: AddProductKind) => {
  const queryClient = useQueryClient();
  const resolveMOrgId = useResolveMOrgId();
  const config = ADD_PRODUCT_CONFIG[kind];

  return useMutation<StepSelectorOption, Error, CreateMRecordArgs>({
    mutationFn: async ({ id, values }) => {
      const attempt = async (retried: boolean): Promise<StepSelectorOption> => {
        const orgId = await resolveMOrgId();
        const body = buildMWriteBody(
          buildMBase({ id, orgId, globalId: id, deviceId: readDeviceId() }),
          buildAddRecordFields(kind, values)
        );
        try {
          const response = await MasterDbService.CreateMDocument(
            config.collection,
            body
          );
          if (!response.data?.length) {
            throw new Error(UPDATE_DEFAULTS_NOT_SAVED_ERROR);
          }
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: [config.mQueryKey] }),
            queryClient.invalidateQueries({
              queryKey: [config.searchQueryKey],
            }),
          ]);
          return buildAddRecordOption(kind, id, values);
        } catch (err) {
          if (isForbidden(err) && !retried) {
            clearMOrgAccessCache(queryClient);
            return attempt(true);
          }
          throw err;
        }
      };
      return attempt(false);
    },
  });
};
